import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Users, Plus, Edit2, Trash2, Loader2, X, Eye, EyeOff } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

const EMPTY = { name_ar: "", name_en: "", role_ar: "", role_en: "", bio_ar: "", bio_en: "", image_url: "", display_order: 0, is_active: true };

export default function AdminTeam() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/team").then(r => setRows(r.data || []));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (t) => setEditing({ ...t });
  const close = () => setEditing(null);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/team/${editing.id}`, editing);
      else await api.post("/admin/team", editing);
      toast.success("Saved");
      close(); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Remove ${t.name_en}?`)) return;
    try { await api.delete(`/admin/team/${t.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleVisible = async (t) => {
    try { await api.put(`/admin/team/${t.id}/toggle`, { is_active: !t.is_active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-team">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <span className="eyebrow">Team</span>
          <h1 className="mt-2 text-3xl text-navy flex items-center gap-3">
            <Users size={26} className="text-gold" /> Leadership Team
          </h1>
        </div>
        <button onClick={openNew} className="btn-gold" data-testid="team-add"><Plus size={14} /> Add member</button>
      </div>
      <span className="divider-gold" />

      <div className="card-elev overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-navy text-cream"><tr>
            <th className="text-start p-3">Order</th>
            <th className="text-start p-3">Name (EN)</th>
            <th className="text-start p-3">Name (AR)</th>
            <th className="text-start p-3">Role</th>
            <th className="text-start p-3">Active</th>
            <th></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-navy/50">No team members yet — click Add.</td></tr>
            ) : rows.map(t => (
              <tr key={t.id} data-testid={`team-row-${t.id}`} className="border-t border-navy/10 hover:bg-cream">
                <td className="p-3 text-navy/70">{t.display_order}</td>
                <td className="p-3 text-navy font-medium">{t.name_en}</td>
                <td className="p-3 text-navy/80">{t.name_ar}</td>
                <td className="p-3 text-navy/70 text-xs">{t.role_en} / {t.role_ar}</td>
                <td className="p-3">
                  {t.is_active ? <Badge className="bg-emerald-100 text-emerald-800"><Eye size={11} className="inline" /> Live</Badge>
                              : <Badge className="bg-navy/10 text-navy/60"><EyeOff size={11} className="inline" /> Hidden</Badge>}
                </td>
                <td className="p-3 text-end whitespace-nowrap">
                  <button onClick={() => toggleVisible(t)} className="text-navy/70 hover:text-gold p-1.5" title={t.is_active ? "Hide from About page" : "Show on About page"} data-testid={`team-toggle-${t.id}`}>
                    {t.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button onClick={() => openEdit(t)} className="text-navy/70 hover:text-gold p-1.5" data-testid={`team-edit-${t.id}`}><Edit2 size={14} /></button>
                  <button onClick={() => remove(t)} className="text-red-600/80 hover:text-red-700 p-1.5" data-testid={`team-del-${t.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white max-w-2xl w-full rounded-md border border-gold/30 p-8 max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()} data-testid="team-editor">
            <div className="flex justify-between items-start">
              <h2 className="text-2xl text-navy">{editing.id ? "Edit member" : "Add member"}</h2>
              <button onClick={close} className="text-navy/50 hover:text-navy"><X size={18} /></button>
            </div>
            <span className="divider-gold" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input className="field-box" placeholder="Name (AR)" value={editing.name_ar} onChange={e => setEditing({...editing, name_ar: e.target.value})} data-testid="team-name_ar" />
              <input className="field-box" placeholder="Name (EN)" value={editing.name_en} onChange={e => setEditing({...editing, name_en: e.target.value})} data-testid="team-name_en" />
              <input className="field-box" placeholder="Role (AR)" value={editing.role_ar || ""} onChange={e => setEditing({...editing, role_ar: e.target.value})} data-testid="team-role_ar" />
              <input className="field-box" placeholder="Role (EN)" value={editing.role_en || ""} onChange={e => setEditing({...editing, role_en: e.target.value})} data-testid="team-role_en" />
              <textarea className="field-box sm:col-span-2 min-h-[80px]" placeholder="Bio (AR)" value={editing.bio_ar || ""} onChange={e => setEditing({...editing, bio_ar: e.target.value})} />
              <textarea className="field-box sm:col-span-2 min-h-[80px]" placeholder="Bio (EN)" value={editing.bio_en || ""} onChange={e => setEditing({...editing, bio_en: e.target.value})} />
              <div className="sm:col-span-2">
                <label className="text-xs text-navy/70 mb-2 block">Portrait</label>
                <ImageUploader
                  value={editing.image_url || ""}
                  folder="team"
                  testId="team-image"
                  onChange={(url) => setEditing({ ...editing, image_url: url || "" })}
                />
              </div>
              <input className="field-box" type="number" placeholder="Display order" value={editing.display_order} onChange={e => setEditing({...editing, display_order: Number(e.target.value) || 0})} />
              <label className="flex items-center gap-2 text-navy">
                <input type="checkbox" className="w-5 h-5 accent-gold" checked={editing.is_active} onChange={e => setEditing({...editing, is_active: e.target.checked})} data-testid="team-active" />
                Show on public site
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} disabled={saving} className="btn-gold disabled:opacity-40" data-testid="team-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
              </button>
              <button onClick={close} className="btn-outline-navy">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
